import {
	ChevronDown,
	ChevronRight,
	HelpCircle,
	Pause,
	Play,
	ShieldCheck,
} from "lucide-react";
import {
	type ReactNode,
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react";
import { Badge } from "../atoms/Badge";
import { Button } from "../atoms/Button";
import { Hash } from "../atoms/Hash";
import { Heading } from "../atoms/Heading";
import { Link } from "../atoms/Link";
import { Text } from "../atoms/Text";
import { cn } from "../lib/cn";
import { CopyButton } from "../molecules/CopyButton";
import {
	TRUST_STATUS_COPY,
	TrustBadge,
	deriveTrustStatus,
} from "../molecules/TrustBadge";
import {
	MERKLE_EXPLAINER_SUMMARY,
	MerkleProofExplainerDialog,
} from "./MerkleProofExplainerDialog";
import { MerkleTreeSVG } from "./MerkleTreeSVG";
import { ProofStepList } from "./ProofStepList";

// STATEFUL EXCEPTION: step (visual stepper), technicalOpen (disclosure) and
// explainerOpen (dialog) are purely UI concerns with no business logic.

export interface ProofTreeViewerProps {
	leafHash: string;
	proof: string[];
	merkleRoot: string;
	leafIndex: number;
	txHash?: string | null;
	chainName?: string | null;
	polygonscanUrl?: string | null;
	/** Whether the record's proof verified. Falls back to presence of merkleRoot. */
	verified?: boolean;
	/** Whether the record's fingerprint is published on-chain. Falls back to txHash. */
	anchored?: boolean;
	/** Whether the record failed an integrity check. Overrides every other status. */
	tampered?: boolean;
	/** Other events sealed alongside this one, surfaced in the plain receipt. */
	recordCount?: number;
	/** Human-readable time the event was recorded, surfaced in the plain receipt. */
	recordedAt?: string | null;
	className?: string;
}

const PLAY_INTERVAL_MS = 1500;

/** Derives left/right sibling position from leafIndex bit at each proof depth. */
function derivePosition(leafIndex: number, depth: number): "left" | "right" {
	const idx = Math.floor(leafIndex / 2 ** depth);
	return idx % 2 === 0 ? "right" : "left";
}

function ProofTreeViewer({
	leafHash,
	proof,
	merkleRoot,
	leafIndex,
	txHash,
	chainName,
	polygonscanUrl,
	verified,
	anchored,
	tampered,
	recordCount,
	recordedAt,
	className,
}: ProofTreeViewerProps): ReactNode {
	const totalSteps = proof.length;
	// Open in the resolved (final) state so the published root reads as reached
	// instead of waiting on a manual "Next" click.
	const [step, setStep] = useState(totalSteps);
	const [playing, setPlaying] = useState(false);
	const [technicalOpen, setTechnicalOpen] = useState(false);
	const [explainerOpen, setExplainerOpen] = useState(false);
	const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

	const status = deriveTrustStatus({
		verified: verified ?? Boolean(merkleRoot),
		anchored: anchored ?? Boolean(txHash),
		tampered,
	});
	const isVerified = status === "verified";

	const proofWithPositions = proof.map((hash, i) => ({
		hash,
		position: derivePosition(leafIndex, i),
	}));

	const stopPlay = useCallback((): void => {
		if (intervalRef.current !== null) {
			clearInterval(intervalRef.current);
			intervalRef.current = null;
		}
		setPlaying(false);
	}, []);

	const startPlay = useCallback((): void => {
		setPlaying(true);
		intervalRef.current = setInterval(() => {
			setStep((prev) => {
				if (prev >= totalSteps) {
					stopPlay();
					return prev;
				}
				return prev + 1;
			});
		}, PLAY_INTERVAL_MS);
	}, [totalSteps, stopPlay]);

	function handlePlayPause(): void {
		if (playing) {
			stopPlay();
		} else {
			if (step >= totalSteps) {
				setStep(0);
			}
			startPlay();
		}
	}

	// Cleanup on unmount
	useEffect(() => {
		return (): void => {
			if (intervalRef.current !== null) {
				clearInterval(intervalRef.current);
			}
		};
	}, []);

	// Stop playing when step reaches end
	useEffect(() => {
		if (step >= totalSteps && playing) {
			stopPlay();
		}
	}, [step, totalSteps, playing, stopPlay]);

	const sealedSentence =
		recordCount && recordCount > 0
			? `It was grouped with ${recordCount} other events into a single sealed record.`
			: "It was grouped with other events into a single sealed record.";
	const recordedSentence = recordedAt
		? `This event was added to the record on ${recordedAt}.`
		: "This event was added to the record.";

	return (
		<div
			className={cn(
				"rounded-lg border border-border bg-card p-4 flex flex-col gap-4",
				className,
			)}
			aria-label="Verification"
		>
			{/* Verdict — leads at every breakpoint */}
			<div className="flex flex-col gap-2">
				<div className="flex items-center gap-2">
					<ShieldCheck
						className="h-4 w-4 text-mutedForeground"
						aria-hidden="true"
					/>
					<Heading size="sm">Verification</Heading>
				</div>
				<div className="flex items-start gap-2 flex-wrap">
					<TrustBadge status={status} />
				</div>
				<Text size="sm" textColor="mutedForeground">
					{TRUST_STATUS_COPY[status].verdict}
				</Text>
			</div>

			{/* Plain-language verification receipt — always visible */}
			<ol
				aria-label="Verification receipt"
				className="flex flex-col gap-2 list-decimal pl-5 marker:text-mutedForeground"
			>
				<li>
					<span className="text-sm font-medium text-foreground">Recorded</span>
					<Text size="sm" textColor="mutedForeground">
						{recordedSentence}
					</Text>
				</li>
				<li>
					<span className="text-sm font-medium text-foreground">Sealed</span>
					<Text size="sm" textColor="mutedForeground">
						{sealedSentence}
					</Text>
				</li>
				<li>
					<span className="text-sm font-medium text-foreground">Published</span>
					<Text size="sm" textColor="mutedForeground">
						That record's fingerprint was published on a public, independent
						ledger.
					</Text>
					{polygonscanUrl && (
						<Link
							href={polygonscanUrl}
							external={true}
							className="text-sm mt-1"
						>
							Check this record on the public ledger
						</Link>
					)}
				</li>
			</ol>

			{/* Technical details — opt-in progressive disclosure */}
			<div className="flex flex-col gap-2 border-t border-border pt-3">
				<Text size="sm" textColor="mutedForeground">
					{MERKLE_EXPLAINER_SUMMARY}
				</Text>
				<button
					type="button"
					className="self-start inline-flex items-center gap-1 text-sm text-primary underline-offset-2 hover:underline"
					onClick={() => setTechnicalOpen((open) => !open)}
					aria-expanded={technicalOpen}
					aria-controls="proof-technical-details"
				>
					{technicalOpen ? (
						<ChevronDown className="h-4 w-4" aria-hidden="true" />
					) : (
						<ChevronRight className="h-4 w-4" aria-hidden="true" />
					)}
					Show how this was verified (technical details)
				</button>

				{technicalOpen && (
					<div
						id="proof-technical-details"
						className="flex flex-col gap-4 mt-1"
					>
						{/* Explainer entry point */}
						<div className="flex items-center justify-end">
							<button
								type="button"
								className="text-xs text-primary underline-offset-2 hover:underline inline-flex items-center gap-1"
								onClick={() => setExplainerOpen(true)}
								aria-label="What am I looking at? Open explainer"
							>
								<HelpCircle className="h-3 w-3" aria-hidden="true" />
								What am I looking at?
							</button>
						</div>

						{/* Chain / tx info */}
						{(txHash ?? chainName) ? (
							<div className="flex flex-wrap gap-4 border-b border-border pb-3">
								{chainName && (
									<div className="flex items-center gap-1.5">
										<Badge variant="secondary" className="text-xs">
											{chainName}
										</Badge>
									</div>
								)}
								{txHash && (
									<div className="flex items-center gap-1">
										<span className="text-xs text-mutedForeground">Tx:</span>
										<Hash value={txHash} length={4} />
										<CopyButton value={txHash} />
									</div>
								)}
							</div>
						) : null}

						{/* SVG visualizer — shown on md+ viewports */}
						<div className="hidden md:block">
							<MerkleTreeSVG
								leafHash={leafHash}
								proof={proofWithPositions}
								merkleRoot={merkleRoot}
								step={step}
								onStepChange={setStep}
								verified={isVerified}
							/>

							{/* Step controls */}
							{totalSteps > 0 && (
								<div className="flex items-center justify-center gap-3 mt-3 pt-3 border-t border-border">
									<Button
										variant="outline"
										size="sm"
										onClick={() => setStep((s) => Math.max(0, s - 1))}
										disabled={step === 0}
										aria-label="Previous step"
									>
										◀ Prev
									</Button>
									<span className="text-xs text-mutedForeground tabular-nums">
										Step {step} of {totalSteps}
									</span>
									<Button
										variant="outline"
										size="sm"
										onClick={() => setStep((s) => Math.min(totalSteps, s + 1))}
										disabled={step === totalSteps}
										aria-label="Next step"
									>
										Next ▶
									</Button>
									<Button
										variant="outline"
										size="sm"
										onClick={handlePlayPause}
										aria-label={playing ? "Pause auto-play" : "Play auto-step"}
									>
										{playing ? (
											<>
												<Pause className="h-3 w-3 mr-1" aria-hidden="true" />
												Pause
											</>
										) : (
											<>
												<Play className="h-3 w-3 mr-1" aria-hidden="true" />
												Play
											</>
										)}
									</Button>
								</div>
							)}
						</div>

						{/* Fallback step list — shown on narrow viewports */}
						<div className="block md:hidden">
							<ProofStepList
								leafHash={leafHash}
								proof={proof}
								merkleRoot={merkleRoot}
								leafIndex={leafIndex}
								chainName={chainName}
								txHash={txHash}
							/>
						</div>
					</div>
				)}
			</div>

			<MerkleProofExplainerDialog
				open={explainerOpen}
				onOpenChange={setExplainerOpen}
			/>
		</div>
	);
}
ProofTreeViewer.displayName = "ProofTreeViewer";

export default ProofTreeViewer;
export { ProofTreeViewer };
